'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import {
  ParticipantChatMessage,
  setParticipantChatMessages,
} from '@/store/slices/participantChatSlice';
import {
  OfficerChatMessage,
  setOfficerChatMessages,
} from '@/store/slices/officerChatSlice';
import { dropUpstreamErrorMessages } from '@/lib/chat/upstreamError';
import { RootState } from '@/store/store';
import {
  participantToDbFormat,
  type ChatTranslationSnapshot,
  type DbChatMessage,
} from '@/lib/chat/chatMessagePersistence';

/**
 * Chat Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for both participant and officer chat messages.
 * - Immediate sync with minimal debounce (100ms) - chat is critical
 * - Hydrates from Supabase on mount
 * - Combines both chat types into single DB table with type discriminator
 *
 * This replaces the archive-only save pattern for chat messages.
 */

const SYNC_DEBOUNCE_MS = 100; // Very short debounce - chat is critical

interface UseChatSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface ChatSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

// The DB shape and the participant converter live in `lib/chat/chatMessagePersistence`
// so the retention invariant they carry can be tested WITHOUT React, Redux,
// Supabase and an auth context in the import graph. `ui-frontend` CI cannot run
// a full pnpm install, so a test that drags this hook's dependencies could only
// ever run on a developer's machine.
// Re-exported because this module's other callers already import them from here.
export type { DbChatMessage, ChatTranslationSnapshot } from '@/lib/chat/chatMessagePersistence';
export { participantToDbFormat } from '@/lib/chat/chatMessagePersistence';

/**
 * Convert Redux OfficerChatMessage to DB schema format
 */
function officerToDbFormat(msg: OfficerChatMessage): DbChatMessage {
  return {
    id: msg.id,
    type: 'officer',
    sender_name: msg.from.name ?? msg.from.identity,
    sender_identity: msg.from.identity,
    message: msg.message,
    timestamp_ms: msg.timestamp,
    tps_results: msg.tpsResults,
  };
}

/**
 * Convert DB format back to ParticipantChatMessage
 */
function dbToParticipantFormat(item: DbChatMessage): ParticipantChatMessage {
  return {
    id: item.id,
    message: item.message,
    timestamp: item.timestamp_ms,
    from: {
      identity: item.sender_identity,
      name: item.sender_name,
      isOfficer: item.is_officer ?? false,
    },
  };
}

/**
 * Convert DB format back to OfficerChatMessage
 */
function dbToOfficerFormat(item: DbChatMessage): OfficerChatMessage {
  return {
    id: item.id,
    message: item.message,
    timestamp: item.timestamp_ms,
    from: {
      identity: item.sender_identity,
      name: item.sender_name,
    },
    tpsResults: item.tps_results as OfficerChatMessage['tpsResults'],
  };
}

/**
 * Hook to sync chat messages to Supabase in real-time
 */
export function useChatSync(
  options: UseChatSyncOptions,
): ChatSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const participantMessages = useSelector((state: RootState) => state.participantChat.messages);
  const officerMessages = useSelector((state: RootState) => state.officerChat.messages);
  const participantTranslations = useSelector(
    (state: RootState) => state.participantChat.translations,
  );

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const stateRef = useReactiveRef<ChatSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useChatSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync chat messages to Supabase
   */
  const syncToSupabase = useCallback(async (
    participantMsgs: ParticipantChatMessage[],
    officerMsgs: OfficerChatMessage[],
    translations: ChatTranslationSnapshot
  ) => {
    if (!enabled || !callId) return;

    // Combine all messages for hashing.
    //
    // ⚠️ The translation is part of the hash. Without it, a translation landing
    // for a message we had already synced would leave the id/timestamp pair
    // unchanged, the hash would match, and the sync would be SKIPPED — so the
    // pairing would render on screen and never reach the record.
    const allMessages = [
      ...participantMsgs.map(m => ({
        id: m.id,
        t: m.timestamp,
        x: translations[m.id]?.state === 'translated' ? translations[m.id]?.text ?? null : null,
      })),
      ...officerMsgs.map(m => ({ id: m.id, t: m.timestamp, x: null })),
    ];
    const hash = JSON.stringify(allMessages.sort((a, b) => a.t - b.t));

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useChatSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      // Convert to DB format and combine
      const dbContent: DbChatMessage[] = [
        ...participantMsgs.map((msg) => participantToDbFormat(msg, translations)),
        ...officerMsgs.map(officerToDbFormat),
      ].sort((a, b) => a.timestamp_ms - b.timestamp_ms);

      // Upsert chat messages
      const { error } = await supabase
        .from('chat_messages')
        .upsert({
          call_id: dbCallId,
          content: dbContent,
        }, {
          onConflict: 'call_id',
        });

      if (error) {
        throw error;
      }

      lastSyncedHashRef.current = hash;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useChatSync', 'Synced chat to Supabase', {
        callId,
        participantCount: participantMsgs.length,
        officerCount: officerMsgs.length,
      });
    } catch (err: unknown) {
      logger.error('useChatSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  // Refs to access current messages without causing dependency changes
  const participantMessagesRef = useRef(participantMessages);
  const officerMessagesRef = useRef(officerMessages);

  // Keep refs updated atomically
  useEffect(() => {
    participantMessagesRef.current = participantMessages;
    officerMessagesRef.current = officerMessages;
  }, [participantMessages, officerMessages]);

  // Track if initial load has been done to prevent re-running
  const initialLoadDoneRef = useRef(false);

  /**
   * Load chat messages from Supabase (on mount)
   * BUG FIX: Removed participantMessages and officerMessages from dependencies
   * to prevent infinite loop. Now uses refs to access current messages.
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean; signal?: AbortSignal }) => {
    if (!enabled || !callId) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    // Prevent re-running after initial load — unless force-reloaded on reconnect (#725)
    if (!opts?.force && initialLoadDoneRef.current) {
      return;
    }
    initialLoadDoneRef.current = true;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      const { data: chatData, error } = await supabase
        .from('chat_messages')
        .select('content')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (chatData?.content && mountedRef.current && !opts?.signal?.aborted) {
        const content = chatData.content as DbChatMessage[];

        // Split by type
        const participantDbMsgs = content.filter(m => m.type === 'participant');
        const officerDbMsgs = content.filter(m => m.type === 'officer');

        const participantMsgs = participantDbMsgs.map(dbToParticipantFormat);
        const officerMsgs = officerDbMsgs.map(dbToOfficerFormat);

        // Merge with any new messages received while loading (use refs to avoid dependency)
        const currentParticipant = participantMessagesRef.current;
        const currentOfficer = officerMessagesRef.current;

        const existingParticipantIds = new Set(participantMsgs.map(m => m.id));
        const existingOfficerIds = new Set(officerMsgs.map(m => m.id));

        const newParticipant = currentParticipant.filter(m => !existingParticipantIds.has(m.id));
        const newOfficer = currentOfficer.filter(m => !existingOfficerIds.has(m.id));

        const mergedParticipant = [...participantMsgs, ...newParticipant].sort(
          (a, b) => a.timestamp - b.timestamp
        );
        const mergedOfficer = [...officerMsgs, ...newOfficer].sort(
          (a, b) => a.timestamp - b.timestamp
        );

        dispatch(setParticipantChatMessages(mergedParticipant));
        dispatch(setOfficerChatMessages(dropUpstreamErrorMessages(mergedOfficer)));

        // Update hash
        const allMessages = [
          ...mergedParticipant.map(m => ({ id: m.id, t: m.timestamp })),
          ...mergedOfficer.map(m => ({ id: m.id, t: m.timestamp })),
        ];
        lastSyncedHashRef.current = JSON.stringify(allMessages.sort((a, b) => a.t - b.t));

        logger.info('useChatSync', 'Loaded from Supabase', {
          participant: mergedParticipant.length,
          officer: mergedOfficer.length,
        });
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useChatSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch]);

  // Load from Supabase on mount — abort if effect re-runs or component unmounts
  useEffect(() => {
    if (!enabled || authLoading) return;

    const abortController = new AbortController();
    loadFromSupabase({ signal: abortController.signal });

    return () => { abortController.abort(); };
  }, [enabled, authLoading, loadFromSupabase]);

  // #725: force a re-sync from Supabase on LiveKit reconnect. The merge keeps
  // local-only messages (by ID via the live participant/officer refs) while
  // backfilling chat sent during the disconnect window. isReloadingRef collapses
  // back-to-back reconnects into a single in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Debounced sync when messages change
  useEffect(() => {
    if (!enabled || !callId) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase(participantMessages, officerMessages, participantTranslations);
    }, debounceMs);
  }, [enabled, callId, participantMessages, officerMessages, participantTranslations, debounceMs, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate chat messages from storage
 */
export function useChatHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const supabase = createClient();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (callData?.id) {
        const { data: chatData } = await supabase
          .from('chat_messages')
          .select('content')
          .eq('call_id', callData.id)
          .maybeSingle();

        if (chatData?.content) {
          const content = chatData.content as DbChatMessage[];

          const participantMsgs = content
            .filter(m => m.type === 'participant')
            .map(dbToParticipantFormat);
          const officerMsgs = content
            .filter(m => m.type === 'officer')
            .map(dbToOfficerFormat);

          dispatch(setParticipantChatMessages(participantMsgs));
          dispatch(setOfficerChatMessages(dropUpstreamErrorMessages(officerMsgs)));

          logger.info('useChatHydration', 'Hydrated from Supabase', {
            participant: participantMsgs.length,
            officer: officerMsgs.length,
          });
        }
      }

      hydratedRef.current = true;
    } catch (err: unknown) {
      logger.error('useChatHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useChatSync;
